import {LabelToken} from '@github-ui/label-token'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {Link} from '@primer/react'
import {Tooltip} from '@primer/react/next'
import {clsx} from 'clsx'
import {useMemo} from 'react'
import {graphql} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import {VALUES} from '../constants/values'
import type {LabelData$key} from './__generated__/LabelData.graphql'
import labelStyles from './Label.module.css'
import styles from './labels.module.css'

type LabelProps = {
  queryRef: LabelData$key
  timelineEventBaseUrl: string
}

export function Label({queryRef, timelineEventBaseUrl}: LabelProps): React.ReactElement {
  const {nameHTML, name, color, id, description} = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment LabelData on Label {
        nameHTML
        name
        color
        id
        description
      }
    `,
    queryRef,
  )

  const url = useMemo(() => {
    return `${timelineEventBaseUrl}?q=${encodeURIComponent(VALUES.labelQuery(name))}`
  }, [timelineEventBaseUrl, name])

  return (
    <span className={clsx(labelStyles.labelDescriptionTooltip, !description && labelStyles.tooltipHidden)}>
      <Tooltip text={description ?? ''}>
        <Link href={url} className={styles.labelLink} aria-describedby={`${id}-tooltip`} muted>
          <LabelToken
            /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side. TODO: Move type assertion to GraphQL fragment LabelData.nameHTML */
            text={<VerifiedHTMLText html={nameHTML as SafeHTMLString} />}
            fillColor={`#${color}`}
            key={id}
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '20ch',
              cursor: 'pointer',
            }}
          />
          <span className="sr-only" id={`${id}-tooltip`}>
            {description ?? ''}
          </span>
        </Link>
      </Tooltip>
    </span>
  )
}
